<template>
  <!--
    Jedan klizac za sve trake na sajtu: logika (strane, strelice, tackice) je ovde,
    a izgled daje komponenta koja ga koristi (kartica u slotu #item i klase za omotace i dugmad).
    Kartice su u HTML-u sa servera (SEO); tackice se crtaju tek posle merenja na klijentu.
  -->
  <div class="container-pages">
    <SectionTitleWithSubTitle :title="title" :classes="titleClass" />

    <!-- npr. "Cene vaze za nedelju" ispod naslova -->
    <slot name="header" />

    <!-- slider-body: position relative (strelice i tackice su absolute) i sopstveni blok formatiranja, kao ranije kolona -->
    <div class="slider-body mt-3" :class="bodyClass">
      <div :class="frameClass">
        <template v-if="items.length">
          <div ref="track" class="products-track" :class="trackClass" role="list" @scroll.passive="onScroll">
            <div
              v-for="(item, index) in items"
              :key="keyOf(item, index)"
              :class="typeof itemClass === 'function' ? itemClass(item, index) : itemClass"
              role="listitem"
            >
              <slot name="item" :item="item" :index="index" />
            </div>
          </div>

          <button
            v-if="!hideEdgeArrows || !atStart"
            type="button"
            class="swiper-button-prev"
            :class="btnPrevClass"
            aria-label="Prethodne"
            :aria-disabled="atStart"
            @click="goToPage(activePage - 1)"
          >
            <i class="esei icon-angle-left-solid" :class="iconClass" aria-hidden="true"></i>
          </button>

          <button
            v-if="!hideEdgeArrows || !atEnd"
            type="button"
            class="swiper-button-next"
            :class="btnNextClass"
            aria-label="Sledeće"
            :aria-disabled="atEnd"
            @click="goToPage(activePage + 1)"
          >
            <i class="esei icon-angle-right-solid" :class="iconClass" aria-hidden="true"></i>
          </button>

          <div v-if="showDots" class="my-swiper-pagination" :class="dotsClass">
            <button
              v-for="n in pageCount"
              :key="n"
              type="button"
              class="pg-dot"
              :class="{ 'is-active': (n - 1) === activePage }"
              :aria-label="`Strana ${n}`"
              :aria-current="(n - 1) === activePage ? 'true' : null"
              @click="goToPage(n - 1)"
            ></button>
          </div>
        </template>

        <slot v-else name="empty" />
      </div>
    </div>
  </div>
</template>

<script setup>
import SectionTitleWithSubTitle from '~/components/cms/product/SectionTitleWithSubTitle.vue'

const props = defineProps({
  items: { type: Array, default: () => [] },
  title: { type: String, default: '' },
  // kljuc stavke: ime polja ili funkcija (item, index) => kljuc
  itemKey: { type: [String, Function], default: 'id' },

  // izgled: klase naslova, omotaca stavke (ili funkcija (item, index) => klase), tela, okvira i trake, strelica, ikonica i tackica
  titleClass: { type: String, default: 'section-title mb-55' },
  itemClass: { type: [String, Array, Object, Function], default: '' },
  bodyClass: { type: [String, Array, Object], default: 'swiper-products' },
  frameClass: { type: [String, Array, Object], default: '' },
  trackClass: { type: [String, Array, Object], default: '' },
  btnPrevClass: { type: [String, Array, Object], default: '' },
  btnNextClass: { type: [String, Array, Object], default: '' },
  iconClass: { type: [String, Array, Object], default: '' },
  dotsClass: { type: [String, Array, Object], default: '' },

  // strelica na pocetku / kraju trake se krije (umesto da posivi)
  hideEdgeArrows: { type: Boolean, default: false },
  // na ovoj sirini prozora i vecoj nema tackica
  dotsHideFromWidth: { type: Number, default: 2550 },
})

const keyOf = (item, index) => {
  if (typeof props.itemKey === 'function') return props.itemKey(item, index)
  return item?.[props.itemKey] ?? index
}

const track = ref(null)

// sve dimenzije se citaju samo u measure() (posle montiranja i kad se traka promeni)
const measured = ref(false)
const pageStarts = ref([0])  // scrollLeft na kome pocinje svaka strana
const scrollLeft = ref(0)
const maxScroll = ref(0)
const wide = ref(false)

const pageCount = computed(() => pageStarts.value.length)

const atStart = computed(() => scrollLeft.value <= 2)
// pre merenja strelica "dalje" nije siva (server ne zna sirinu ekrana)
const atEnd = computed(() => measured.value && scrollLeft.value >= maxScroll.value - 2)

const activePage = computed(() => {
  if (!measured.value) return 0
  if (atEnd.value) return pageCount.value - 1

  // poslednja strana koja je pocela do trenutnog polozaja
  let page = 0
  pageStarts.value.forEach((start, index) => {
    if (start <= scrollLeft.value + 2) page = index
  })
  return page
})

const showDots = computed(() => measured.value && !wide.value && pageCount.value > 1)

// strane iz stvarnih pozicija kartica (rade i kad su kartice razlicite sirine):
// nova strana pocinje od prve kartice koja ne staje cela; kartica sira od trake je sama svoja strana;
// ostatak manji od cetvrtine kartice ne pravi novu stranu, a poslednja strana je kraj trake
function buildPages(el, max) {
  const kids = [...el.children]
  if (!kids.length || max <= 2) return [0]

  const first = kids[0].offsetLeft
  const pos = kids.map((kid) => kid.offsetLeft - first)
  const widths = kids.map((kid) => kid.offsetWidth)
  const view = el.clientWidth
  const minRest = widths[0] / 4

  const starts = [0]
  while (starts.length <= kids.length) {
    const start = starts[starts.length - 1]
    if (max - start <= minRest) break

    const next = pos.find((p, i) => p > start + 1 && p + widths[i] > start + view + 1)

    // sve sledece kartice staju; ako ne staje samo kartica od koje strana pocinje (sira od trake), nema nove strane
    if (next === undefined && pos.some((p, i) => Math.abs(p - start) <= 1 && widths[i] > view + 1)) break

    let left = next === undefined ? max : Math.min(next, max)
    if (max - left <= minRest) left = max
    if (left <= start) break

    starts.push(left)
    if (left >= max) break
  }

  return starts
}

function measure() {
  const el = track.value
  if (!el) return

  maxScroll.value = Math.max(0, el.scrollWidth - el.clientWidth)
  pageStarts.value = buildPages(el, maxScroll.value)
  scrollLeft.value = el.scrollLeft
  wide.value = window.innerWidth >= props.dotsHideFromWidth
  measured.value = true
}

// skrol: samo scrollLeft, najvise jednom po frejmu (bez citanja stilova i dimenzija)
let raf = 0
function onScroll() {
  if (raf) return
  raf = requestAnimationFrame(() => {
    raf = 0
    if (track.value) scrollLeft.value = track.value.scrollLeft
  })
}

function goToPage(page) {
  const el = track.value
  if (!el || !measured.value) return

  const target = Math.max(0, Math.min(pageCount.value - 1, page))
  el.scrollTo({ left: pageStarts.value[target], behavior: 'smooth' })
}

let ro = null

function observe() {
  if (ro) ro.disconnect()
  if (track.value && 'ResizeObserver' in window) {
    ro = new ResizeObserver(() => measure())
    ro.observe(track.value)
  }
}

onMounted(() => {
  measure()
  observe()
})

// nove stavke (npr. posle pracenja ili filtera) menjaju strane; traka se pojavljuje kad stignu prve stavke
watch(() => props.items.length, () => nextTick(() => {
  measure()
  observe()
}))

onBeforeUnmount(() => {
  if (ro) ro.disconnect()
  if (raf) cancelAnimationFrame(raf)
})
</script>
